import { Breadcrumb, BreadcrumbItem } from "@carbon/react";
import { capitalize, toDate } from "@flaminem/utils";
import { FC } from "react";
import { Link, useParams } from "react-router-dom";

import { BackButtonWithCondition } from "src/components/Buttons";
import Loading from "src/components/DataStates/Loading.tsx";
import InlineError from "src/components/Errors/InlineError";
import PageHeader from "src/components/PageHeader.tsx";
import ScenarioRulesTable from "src/components/scenario/ScenarioRulesTable/ScenarioRulesTable.tsx";
import { useGetFullScenarioById } from "src/graphql/anti-fraud/queries/scenarios/GetFullScenarioQuery";
import { useFormatDate, useT } from "src/internationalization";
import { isDefinedScenarioRule } from "src/types/scenario";

export const ScenarioVersionDetailsPage: FC = () => {
  const param = useParams<{ id: string; versionId: string; caseTypeCode: string }>();
  const t = useT();
  const formatDate = useFormatDate();

  const { data: scenario, loading, error } = useGetFullScenarioById({ id: param.id });

  const archivedScenario = scenario?.archivedVersions.find(
    (version) => version.id === param.versionId
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <InlineError message={error?.message} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <Breadcrumb noTrailingSlash={true}>
        <BreadcrumbItem>
          <Link to="/scenarios/requests">{t("scenarios.title")}</Link>
        </BreadcrumbItem>
        {scenario && (
          <BreadcrumbItem>
            <Link to={`/scenario/${param.caseTypeCode}/${scenario.id}/archived`}>
              {scenario.name}
            </Link>
          </BreadcrumbItem>
        )}
        {archivedScenario?.name && (
          <BreadcrumbItem isCurrentPage={true}>
            {`${t("scenario.archived.label")} - ${archivedScenario.name}`}
          </BreadcrumbItem>
        )}
      </Breadcrumb>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <BackButtonWithCondition />
          <PageHeader>
            {t("scenario.archived.title")} - {archivedScenario?.name}
          </PageHeader>
        </div>
      </div>
      {archivedScenario ? (
        <div className="h-[100vh]">
          <div className="flex flex-col gap-5 h-full">
            <div>
              <p>
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("scenario.tab.label.threshold")}:{" "}
                </span>
                {archivedScenario.threshold}
              </p>
              <p>
                <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                  {t("scenario.tab.label.date")}:{" "}
                </span>
                {formatDate(toDate(archivedScenario.adminStatus.creation), { dateStyle: "medium" })}
              </p>
              {archivedScenario.description && (
                <div className="line-clamp-3 text-body02 leading-body02 tracking-body02 font-body02">
                  <span className="text-label02 text-textSecondary leading-label02 tracking-label02 font-label02">
                    {t("general.table.description.column.title")}:{" "}
                  </span>
                  {capitalize(archivedScenario.description)}
                </div>
              )}
            </div>
            <ScenarioRulesTable
              rules={archivedScenario.rules.filter(isDefinedScenarioRule).map((details) => ({
                ...details.rule,
                score: details.score,
              }))}
            />
          </div>
        </div>
      ) : (
        <div>{t("general.empty")}</div>
      )}
    </div>
  );
};
